import { GOOGLE_VOLUME_LANGUAGES, type GoogleVolumeTarget } from './query-volume.js';

export type VolumeContext =
  | { source: 'wordstat'; regionCode: string; regionName: string; device: 'desktop' | 'mobile' }
  | {
      source: 'google_ads';
      regionName: string;
      geoTargetId: string;
      languageId: GoogleVolumeTarget['languageId'];
    };

// SERP provider, depth, mode and profile ID never identify a frequency measurement.
export function volumeContext(
  profile: {
    searchEngine: string;
    regionCode: string;
    regionName: string;
    device: 'desktop' | 'mobile';
  },
  target?: GoogleVolumeTarget | null,
): VolumeContext | null {
  return profile.searchEngine === 'google'
    ? target
      ? { source: 'google_ads', ...target, regionName: `Google Ads: ${target.geoTargetId}` }
      : null
    : {
        source: 'wordstat',
        regionCode: profile.regionCode,
        regionName: profile.regionName,
        device: profile.device,
      };
}

export function volumeContextKey(context: VolumeContext): string {
  return JSON.stringify(
    context.source === 'wordstat'
      ? [context.source, context.regionCode, context.device]
      : [context.source, context.geoTargetId, context.languageId],
  );
}
export function sameVolumeContext(a: VolumeContext | null, b: VolumeContext | null): boolean {
  return !!a && !!b && volumeContextKey(a) === volumeContextKey(b);
}
export function parseVolumeContext(raw: string): VolumeContext {
  const value: unknown = JSON.parse(raw);
  if (
    value &&
    typeof value === 'object' &&
    'regionName' in value &&
    typeof value.regionName === 'string' &&
    value.regionName.trim().length > 0 &&
    value.regionName.length <= 200 &&
    'source' in value
  ) {
    if (
      value.source === 'wordstat' &&
      'regionCode' in value &&
      typeof value.regionCode === 'string' &&
      /^\d+$/.test(value.regionCode) &&
      'device' in value &&
      (value.device === 'desktop' || value.device === 'mobile')
    )
      return {
        source: value.source,
        regionName: value.regionName,
        regionCode: value.regionCode,
        device: value.device,
      };
    if (
      value.source === 'google_ads' &&
      'geoTargetId' in value &&
      typeof value.geoTargetId === 'string' &&
      /^\d{1,20}$/.test(value.geoTargetId) &&
      'languageId' in value &&
      typeof value.languageId === 'string' &&
      Object.hasOwn(GOOGLE_VOLUME_LANGUAGES, value.languageId)
    )
      return {
        source: value.source,
        regionName: value.regionName,
        geoTargetId: value.geoTargetId,
        languageId: value.languageId as GoogleVolumeTarget['languageId'],
      };
  }
  throw new Error('Сохранён некорректный контекст частотности');
}
